import React, { forwardRef } from "react";

import ChatBubbleIcon from "@uui/MessageCircle02";
import RefreshIcon from "@uui/RefreshCcw05";

import type { Theme } from "@mui/material";
import Button, { ButtonProps } from "@mui/material/Button";
import Container, { ContainerProps } from "@mui/material/Container";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import {
  BaseHeaderContainer,
  BaseOuterContainer,
  ContentColumn,
} from "@app/analytics/components/reports/styles";
import type { Topic, TopicChannelProvider } from "@app/analytics/types";

import Stack, { Row, StackProps } from "@rui/Stack";

type OuterContainerProps = StackProps & { heightOffset: number };

export const OuterContainer = styled<React.FC<OuterContainerProps>>(
  ({ heightOffset: _ho, ...props }) => <BaseOuterContainer {...props} />
)(({ theme, heightOffset }) => ({
  "& .topic-report__inner-container": {
    height: `calc(100vh - ${heightOffset}px)`,
  },
  [theme.breakpoints.down("mobile")]: {
    overflowY: "hidden",
  },
}));

export const MainContentContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3, 2),
}));

type HeaderContainerProps = StackProps & { provider: TopicChannelProvider };

const getDarkProviderStyle = ({
  theme,
  fontFamily,
}: {
  theme: Theme;
  fontFamily: React.CSSProperties["fontFamily"];
}) => ({
  color: "#FFF",
  "& #topic-report-title": {
    color: "#FFF",
    fontFamily: `${fontFamily}, Work Sans, sans-serif`,
    fontWeight: 700,
  },
  "& .header__sources": {
    color: theme.palette.transparent.white[40],
    fontFamily: `${fontFamily}, Work Sans, sans-serif`,
    fontWeight: 700,
  },
  "& .header__date-and-time": {
    fontFamily: `${fontFamily}, Work Sans, sans-serif`,
  },
  "& .editable-typography, .editable-typography--clickable": {
    "&:hover": {
      backgroundColor: `${theme.palette.transparent.black[30]} !important`,
    },
    "& .editable-typography__edit-button:not(.editing)": {
      backgroundColor: `${theme.palette.transparent.black[30]} !important`,
    },
    "& .editable-typography__input": {
      color: "#FFF",
      input: {
        fontFamily: `${fontFamily}, Work Sans, sans-serif`,
      },
    },
  },
  "& .header-button__back:hover": {
    backgroundColor: theme.palette.transparent.black[30],
  },
  "& .header-button": {
    fontWeight: 700,
    fontFamily: `${fontFamily}, Work Sans, sans-serif`,
    color: "#FFF",
    "&.MuiButton-contained": {
      backgroundColor: theme.palette.transparent.black[30],
      "&:hover": {
        color: "#FFF",
        backgroundColor: theme.palette.transparent.black[50],
      },
    },
    "&.MuiButton-outlined": {
      "&:hover": {
        color: "#FFF",
        backgroundColor: theme.palette.transparent.black[30],
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.transparent.black[16],
      },
    },
    "&.header-icon-button:hover": {
      backgroundColor: theme.palette.transparent.black[30],
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
  },
  "& .MuiDivider-root": {
    borderColor: theme.palette.transparent.white[12],
  },
});

const getGmailStyle = (theme: Theme) => ({
  backgroundColor: "#EAF1FB",
  "& .header-button": {
    color: theme.palette.shades.neutral[800],
    fontFamily: "Roboto, Work Sans, sans-serif",
    "&.MuiButton-contained": {
      backgroundColor: "#C2E7FF",
    },
    "&.MuiButton-outlined": {
      "&:hover": {
        backgroundColor: theme.palette.transparent.black[4],
      },
      "&.Mui-disabled": {
        backgroundColor: theme.palette.transparent.black[16],
      },
    },
    "&.header-icon-button:hover": {
      backgroundColor: theme.palette.transparent.black[4],
    },
  },
  "& .editable-typography, .editable-typography--clickable": {
    "&:hover": {
      backgroundColor: theme.palette.transparent.black[4],
    },
    "& .editable-typography__edit-button:not(.editing)": {
      backgroundColor: theme.palette.transparent.black[4],
    },
  },
  "& #topic-report-title, .header__sources, .editable-typography__input input, .header__date-and-time":
    {
      fontFamily: "Roboto, Work Sans, sans-serif",
    },
});

const HeaderContainerWithRef = React.forwardRef<
  HTMLDivElement,
  HeaderContainerProps
>(({ provider: _p, ...props }, ref) => (
  <BaseHeaderContainer {...props} ref={ref} />
));
HeaderContainerWithRef.displayName = "HeaderContainerWithRef";

export const HeaderContainer = styled(HeaderContainerWithRef)(
  ({ theme, provider }) => ({
    borderBottom: "1px solid",
    borderColor: theme.palette.shades.neutral[100],
    color: theme.palette.shades.neutral[800],
    "& .header__sources": {
      color: theme.palette.transparent.black[40],
    },
    ...(provider === "microsoft_graph_teams_chat_ingest" && {
      backgroundColor: "#464775",
      ...getDarkProviderStyle({ theme, fontFamily: "Open Sans" }),
    }),
    ...(provider === "microsoft_graph_email" && {
      backgroundColor: "#316AB7",
      ...getDarkProviderStyle({ theme, fontFamily: "Open Sans" }),
    }),
    ...(provider === "slack_ingest" && {
      backgroundColor: "#4A154B",
      ...getDarkProviderStyle({ theme, fontFamily: "Lato" }),
    }),
    ...(provider === "google_email" && getGmailStyle(theme)),

    [theme.breakpoints.down("sm")]: {
      alignItems: "start",
    },
  })
);

type HeaderStatusChipProps = StackProps & {
  status: Topic["status"];
};

export const HeaderStatusChip = styled<React.FC<HeaderStatusChipProps>>(
  ({ children, status, ...props }) => (
    <Row variant="left" spacing={0.5} {...props}>
      {status === "active" && <RefreshIcon sx={{ fontSize: "12px" }} />}
      <Typography variant="labelMed">{children}</Typography>
    </Row>
  )
)(({ status, theme }) => ({
  borderRadius: "8px",
  padding: "2px 8px",
  backgroundColor:
    status === "inactive"
      ? theme.palette.shades.neutral[100]
      : theme.palette.shades.positive[100],
  color:
    status === "inactive"
      ? theme.palette.shades.neutral[500]
      : theme.palette.shades.positive[600],
  "&:hover": {
    cursor: "default",
  },
}));

export const TopicReportChip = styled<React.FC<TypographyProps>>((props) => (
  <Typography variant="labelMed" {...props}>
    Readout
  </Typography>
))(({ theme }) => ({
  borderRadius: "8px",
  background: "linear-gradient(84.04deg, #6534CC 0%, #31B7BF 99.17%)",
  color: "#FFF",
  padding: "0 6px",
  whiteSpace: "nowrap",
  [theme.breakpoints.up("mobile")]: {
    padding: "2px 8px",
  },
}));

type ContentColumnProps = StackProps;

const ContentColumnWithRef = forwardRef<
  HTMLDivElement | null,
  ContentColumnProps
>(({ ...props }, ref) => <ContentColumn {...props} ref={ref} />);

ContentColumnWithRef.displayName = "ContentColumnWithRef";

export const TopicContentColumn = styled(ContentColumnWithRef)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {},
}));

export const MenuButton = styled<React.FC<ButtonProps>>((props) => (
  <Button {...props} variant="outlined" />
))(({ theme }) => ({
  borderColor: theme.palette.shades.neutral[100],
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[500],
  padding: theme.spacing(1),
  backgroundColor: "#FFF",
  "& .MuiButton-endIcon": {
    marginRight: 0,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.shades.neutral[500],
    "& svg": {
      fontSize: "16px",
    },
  },
  "&.Mui-disabled .MuiButton-endIcon": {
    color: theme.palette.shades.neutral[200],
  },
}));

export const SubSectionContainer = styled<React.FC<ContainerProps>>((props) => (
  <Container maxWidth="md" disableGutters {...props} />
))({});

type SourceCountChipProps = StackProps & {
  count: number;
};

export const SourceCountChip = styled<React.FC<SourceCountChipProps>>(
  (props) => (
    <Row alignItems="center" gap={0.25}>
      <ChatBubbleIcon sx={{ height: "12px", width: "12px" }} />
      <Typography lineHeight="12px" variant="labelReg">
        {props.count}
      </Typography>
    </Row>
  )
)({});
